/**
 * Bausteine des Lieferverzug-Reiters: Karte, Kacheln, Legende, Tabellen-Umschalter.
 *
 * Bewusst zurueckhaltend gehalten — die Daten sind das Laute. Hairline-Raender,
 * viel Weissraum, CI-Gelb nur als Marker (Reiter, Leitzahl-Unterstrich), nie als
 * Datenfarbe. Inline-Styles wie im Rest der beiden Tools.
 */

import {
  Children, cloneElement, isValidElement, useEffect, useState,
  type ReactElement, type ReactNode,
} from 'react';
import { Table2, BarChart3, Info } from 'lucide-react';
import { FARBEN, KLASSEN, prozent, quoteText, zahl, bewerteLeitzahl } from './viz';
import type { Quote } from './typen';

// ─── Schmale Bildschirme (Handy) ─────────────────────────────────────────────
//
// Die Seite wird auch am Handy gelesen — der Lieferant bekommt den externen
// Link per Mail (Wunsch Matze 03.09.2026). Zwei Dinge braucht das:
//
//   1. TABELLEN werden zu Zeilen-Bloecken. Vier bis fuenf Spalten in ~300 px
//      hiessen vorher: seitlich wischen und dabei die Auftragsnummer aus dem
//      Blick verlieren. Untereinander mit Beschriftung davor liest sich jede
//      Zeile wie ein kleiner Steckbrief.
//   2. Etwas mehr NUTZBREITE: Karten- und Seitenpolster schrumpfen, damit die
//      Diagramme nicht auf 250 px zusammenfallen.
//
// Umgesetzt als EIN eingehaengtes Stylesheet mit Media Query (nicht als
// JS-Breakpoint): so greift es beim Drehen des Geraets sofort und ohne
// Re-Render. `useIstSchmal()` gibt es zusaetzlich fuer die Faelle, in denen
// CSS nicht reicht — recharts braucht Zahlen, keine Klassen.

/** Ab hier gilt "schmal". 640 px = Handy quer/hoch, Tablet bleibt normal. */
export const SCHMAL_GRENZE = 640;

const STIL_ID = 'lieferverzug-schmal-stile';

const SCHMAL_CSS = `
@media (max-width: ${SCHMAL_GRENZE}px) {
  .lv-karte-kopf { padding: 0.85rem 0.85rem 0.6rem !important; flex-wrap: wrap; }
  .lv-karte-inhalt { padding: 0 0.85rem 0.9rem !important; }
  .lv-tab-huelle { overflow-x: visible; }
  .lv-tab thead { display: none; }
  .lv-tab tbody tr {
    display: block;
    padding: 0.55rem 0;
    border-bottom: 1px solid ${FARBEN.raster};
  }
  .lv-tab tbody tr:last-child { border-bottom: none; }
  .lv-tab td {
    display: flex !important;
    align-items: baseline;
    gap: 0.6rem;
    padding: 0.12rem 0 !important;
    border-bottom: none !important;
    text-align: left !important;
    white-space: normal !important;
    min-width: 0 !important;
  }
  .lv-tab td[data-label]:not([data-label=""])::before {
    content: attr(data-label);
    flex: 0 0 6.5rem;
    color: ${FARBEN.tinteLeise};
    font-weight: 600;
  }
  /* Erste Spalte ist die Zeilen-Ueberschrift (Auftragsnummer, Artikelname):
     ueber die ganze Breite und ohne Beschriftung — sonst brechen lange
     Artikelnamen in eine 5-zeilige Spalte um. */
  .lv-tab tbody td:first-child {
    display: block !important;
    font-weight: 600;
    color: ${FARBEN.tinteStark};
    margin-bottom: 0.2rem;
  }
  .lv-tab tbody td:first-child::before { content: none !important; }
  /* Balken der Quoten-Zelle darf die Restbreite nehmen */
  .lv-tab td > div { flex: 1; min-width: 0; }
  /* Lange Auftragslisten: mehr Hoehe, damit mehrere Steckbriefe sichtbar sind */
  .lv-liste { max-height: 60vh !important; }
}
`;

function stileEinhaengen(): void {
  if (typeof document === 'undefined' || document.getElementById(STIL_ID)) return;
  const el = document.createElement('style');
  el.id = STIL_ID;
  el.textContent = SCHMAL_CSS;
  document.head.appendChild(el);
}

/** Haengt die Schmal-Regeln ein (idempotent, einmal pro Seite). */
export function useSchmalStile(): void {
  useEffect(stileEinhaengen, []);
}

/**
 * true, solange der Viewport schmaler als `SCHMAL_GRENZE` ist. Nur fuer
 * Werte, die als ZAHL gebraucht werden (recharts-Achsenbreiten, Label-Schalter).
 * Fuer alles andere die CSS-Klassen oben nehmen.
 */
export function useIstSchmal(): boolean {
  const abfrage = () => typeof window !== 'undefined'
    && window.matchMedia(`(max-width: ${SCHMAL_GRENZE}px)`).matches;
  const [schmal, setSchmal] = useState(abfrage);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${SCHMAL_GRENZE}px)`);
    const auf = () => setSchmal(mq.matches);
    mq.addEventListener('change', auf);
    auf();
    return () => mq.removeEventListener('change', auf);
  }, []);
  return schmal;
}

// ─── Karte mit Titel, Frage und Tabellen-Umschalter ──────────────────────────

/**
 * Jede Diagramm-Karte kann auf eine Tabelle umschalten. Das ist kein Extra:
 * ein Tooltip darf nie der einzige Weg zu einem Wert sein, und drei der
 * Status-Farben liegen unter 3:1 Kontrast — die Tabelle ist der Ausgleich.
 */
export function Karte({
  titel, frage, hinweis, tabelle, children,
}: {
  titel: string;
  frage: string;
  hinweis?: string;
  tabelle?: ReactNode;
  children: ReactNode;
}) {
  const [zeigeTabelle, setZeigeTabelle] = useState(false);
  useSchmalStile();
  return (
    <section className="lv-karte" style={{
      background: FARBEN.flaeche, border: `1px solid ${FARBEN.linie}`,
      borderRadius: '10px', marginBottom: '1.25rem',
    }}>
      <header className="lv-karte-kopf" style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: '1rem', padding: '1rem 1.25rem 0.75rem',
      }}>
        {/* min-width 0, damit die Frage umbricht statt den Knopf zu verdraengen */}
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 650, color: FARBEN.tinteStark }}>{titel}</h3>
          <p style={{ margin: '0.15rem 0 0', fontSize: '0.8125rem', color: FARBEN.tinteMittel }}>{frage}</p>
        </div>
        {tabelle && (
          <button
            onClick={() => setZeigeTabelle((v) => !v)}
            title={zeigeTabelle ? 'Diagramm anzeigen' : 'Zahlen als Tabelle anzeigen'}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0,
              padding: '0.3rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer',
              border: `1px solid ${FARBEN.linie}`, borderRadius: '6px',
              background: zeigeTabelle ? FARBEN.seite : FARBEN.flaeche, color: FARBEN.tinteMittel,
            }}
          >
            {zeigeTabelle ? <BarChart3 size={13} /> : <Table2 size={13} />}
            {zeigeTabelle ? 'Diagramm' : 'Tabelle'}
          </button>
        )}
      </header>
      <div className="lv-karte-inhalt" style={{ padding: '0 1.25rem 1.25rem' }}>
        {zeigeTabelle && tabelle ? tabelle : children}
        {hinweis && (
          <p style={{
            margin: '0.75rem 0 0', fontSize: '0.75rem', color: FARBEN.tinteLeise,
            display: 'flex', gap: '0.35rem', alignItems: 'flex-start', lineHeight: 1.5,
          }}>
            <Info size={12} style={{ marginTop: '0.15rem', flexShrink: 0 }} />
            <span>{hinweis}</span>
          </p>
        )}
      </div>
    </section>
  );
}

// ─── "Kurz gesagt"-Kasten ────────────────────────────────────────────────────

/**
 * Ein Satz Klartext unter jedem Diagramm — die Antwort auf "was sagt mir das?".
 * CI-Gelb als linke Kante (Marke, keine Datenfarbe). Feedback Matze 18.08.:
 * die Karten waren "zu theoretisch" — dieser Kasten uebersetzt die Zahlen.
 */
export function FazitBox({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div style={{
      display: 'flex', gap: '0.75rem', alignItems: 'stretch',
      margin: '0.85rem 0 0', background: FARBEN.seite,
      borderRadius: '8px', overflow: 'hidden',
    }}>
      <div style={{ width: '4px', flexShrink: 0, background: FARBEN.akzent }} />
      <p style={{
        margin: 0, padding: '0.6rem 0.85rem 0.6rem 0', fontSize: '0.875rem',
        color: FARBEN.tinteStark, lineHeight: 1.6,
      }}>
        <strong>Kurz gesagt:</strong> {text}
      </p>
    </div>
  );
}

// ─── Legende ─────────────────────────────────────────────────────────────────

export interface LegendenEintrag {
  label: string;
  farbe: string;
  form?: 'flaeche' | 'linie' | 'linieGestrichelt';
}

/** Ab zwei Serien Pflicht — Identitaet darf nie allein an der Farbe haengen. */
export function Legende({ eintraege }: { eintraege: LegendenEintrag[] }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1rem',
      marginBottom: '0.75rem', fontSize: '0.75rem', color: FARBEN.tinteMittel,
    }}>
      {eintraege.map((e) => (
        <span key={e.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          {e.form === 'linie' || e.form === 'linieGestrichelt' ? (
            <span style={{
              width: '18px', height: '0', flexShrink: 0,
              borderTop: `2px ${e.form === 'linieGestrichelt' ? 'dashed' : 'solid'} ${e.farbe}`,
            }} />
          ) : (
            <span style={{ width: '11px', height: '11px', borderRadius: '3px', background: e.farbe, flexShrink: 0 }} />
          )}
          {e.label}
        </span>
      ))}
    </div>
  );
}

export const KLASSEN_LEGENDE: LegendenEintrag[] = KLASSEN.map((k) => ({ label: k.label, farbe: k.farbe }));

// ─── Leitzahl (Hero) ─────────────────────────────────────────────────────────

/**
 * Die eine Zahl, mit der der Reiter aufmacht. Sie traegt ihre Fallzahl und ihr
 * Vertrauensband direkt darunter — eine nackte Prozentzahl aus 6 Faellen waere
 * auf einer Folie eine Falschaussage.
 */
export function Leitzahl({ quote, unterzeile }: { quote: Quote; unterzeile: string }) {
  const bewertung = bewerteLeitzahl(quote);
  return (
    <div style={{
      background: FARBEN.flaeche, border: `1px solid ${FARBEN.linie}`,
      borderRadius: '10px', padding: '1.25rem 1.5rem', flex: '1 1 320px',
    }}>
      <div style={{ fontSize: '0.8125rem', color: FARBEN.tinteMittel, marginBottom: '0.35rem' }}>
        Gehaltene Liefertermine — letzte 8 Wochen
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
        {/* Ohne Messwert KEIN Riesen-Gedankenstrich (der liest sich wie ein
            geschwaerzter Wert) — dann steht da schlicht, dass nichts da ist. */}
        {quote.anteil === null ? (
          <span style={{ fontSize: '1.25rem', lineHeight: 1.4, fontWeight: 600, color: FARBEN.tinteLeise }}>
            noch nicht messbar
          </span>
        ) : (
          <>
            <span style={{ fontSize: '3.25rem', lineHeight: 1, fontWeight: 700, color: FARBEN.tinteStark }}>
              {prozent(quote.anteil)}
            </span>
            <span style={{ fontSize: '0.8125rem', color: FARBEN.tinteLeise }}>
              von {quote.n} {quote.n === 1 ? 'Auftrag' : 'Aufträgen'}
            </span>
          </>
        )}
      </div>
      {/* CI-Gelb als Marke, nicht als Datenfarbe */}
      <div style={{ width: '56px', height: '4px', background: FARBEN.akzent, borderRadius: '2px', margin: '0.6rem 0 0.5rem' }} />
      {quote.anteil !== null && (
        <div style={{ fontSize: '0.8125rem', color: bewertung.farbe, fontWeight: 600 }}>{bewertung.text}</div>
      )}
      <div style={{ fontSize: '0.75rem', color: FARBEN.tinteLeise, marginTop: '0.25rem', lineHeight: 1.5 }}>
        {quoteText(quote)} · {unterzeile}
      </div>
    </div>
  );
}

// ─── Kacheln ─────────────────────────────────────────────────────────────────

export function Kachel({
  label, wert, zusatz, ton = 'neutral', erklaerung,
}: {
  label: string;
  wert: number | string;
  zusatz?: string;
  ton?: 'neutral' | 'warnung' | 'kritisch' | 'gut';
  /** Ausfuehrlicher Erklaertext beim Zeigen auf die Kachel. */
  erklaerung?: string;
}) {
  const tonFarbe = {
    neutral: FARBEN.tinteStark, gut: FARBEN.gut, warnung: FARBEN.ernst, kritisch: FARBEN.kritisch,
  }[ton];
  return (
    <div title={erklaerung} style={{
      background: FARBEN.flaeche, border: `1px solid ${FARBEN.linie}`, borderRadius: '8px',
      padding: '0.7rem 0.85rem', minWidth: 0, cursor: erklaerung ? 'help' : undefined,
    }}>
      <div style={{ fontSize: '0.7rem', color: FARBEN.tinteMittel, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: tonFarbe, lineHeight: 1.1 }}>{wert}</div>
      {zusatz && <div style={{ fontSize: '0.7rem', color: FARBEN.tinteLeise, marginTop: '0.15rem' }}>{zusatz}</div>}
    </div>
  );
}

export function KachelReihe({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'grid', gap: '0.6rem', marginBottom: '1.25rem',
      gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))',
    }}>
      {children}
    </div>
  );
}

// ─── Tabellen ────────────────────────────────────────────────────────────────

/**
 * Spaltenkopf: entweder nur der Text (dann rechtsbuendig ab der zweiten Spalte,
 * weil dort Zahlen stehen) oder `{ label, links: true }` fuer Textspalten weiter
 * hinten — eine rechtsbuendige Textspalte liest sich falsch.
 */
export type Spalte = string | { label: string; links?: boolean };

/**
 * Haengt jeder Zelle die Spaltenbeschriftung als `data-label` an — am Handy
 * steht sie dann vor dem Wert (s. SCHMAL_CSS). Zellen mit `colSpan`
 * (Leer-Zeilen, Zwischentitel) bleiben unbeschriftet.
 */
function mitSpaltenLabel(zeilen: ReactNode, kopf: Spalte[]): ReactNode {
  const labels = kopf.map((h) => (typeof h === 'string' ? h : h.label));
  return Children.map(zeilen, (zeile) => {
    if (!isValidElement(zeile) || zeile.type !== 'tr') return zeile;
    const inhalt = (zeile.props as { children?: ReactNode }).children;
    const zellen = Children.map(inhalt, (zelle, i) => {
      if (!isValidElement(zelle)) return zelle;
      const props = zelle.props as { colSpan?: number; datenLabel?: string };
      if (props.colSpan || props.datenLabel !== undefined) return zelle;
      return cloneElement(zelle as ReactElement<{ datenLabel?: string }>, {
        datenLabel: labels[i] ?? '',
      });
    });
    return cloneElement(zeile as ReactElement<{ children?: ReactNode }>, {}, zellen);
  });
}

export function Tabelle({ kopf, children }: { kopf: Spalte[]; children: ReactNode }) {
  useSchmalStile();
  return (
    <div className="lv-tab-huelle" style={{ overflowX: 'auto' }}>
      <table className="lv-tab" style={{
        width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem',
        fontVariantNumeric: 'tabular-nums',
      }}>
        <thead>
          <tr>
            {kopf.map((h, i) => {
              const label = typeof h === 'string' ? h : h.label;
              const links = typeof h === 'string' ? i === 0 : (h.links ?? i === 0);
              return (
                <th key={label} style={{
                  textAlign: links ? 'left' : 'right', padding: '0.4rem 0.5rem',
                  borderBottom: `1px solid ${FARBEN.linie}`, color: FARBEN.tinteMittel,
                  fontWeight: 600, whiteSpace: 'nowrap',
                }}>{label}</th>
              );
            })}
          </tr>
        </thead>
        <tbody>{mitSpaltenLabel(children, kopf)}</tbody>
      </table>
    </div>
  );
}

export function Zelle({
  children, rechts, leise, monospace, datenLabel,
}: {
  children: ReactNode; rechts?: boolean; leise?: boolean; monospace?: boolean;
  /** Setzt `Tabelle` automatisch — Spaltenname fuer die Handy-Ansicht. */
  datenLabel?: string;
}) {
  return (
    <td data-label={datenLabel} style={{
      padding: '0.35rem 0.5rem', borderBottom: `1px solid ${FARBEN.raster}`,
      textAlign: rechts ? 'right' : 'left',
      color: leise ? FARBEN.tinteLeise : FARBEN.tinteStark,
      fontFamily: monospace ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
      whiteSpace: monospace ? 'nowrap' : undefined,
    }}>
      {children}
    </td>
  );
}

/**
 * Anteil als Zahl PLUS kurzer Balken. Der Balken zeigt auf einen Blick "wie
 * voll", die Prozentzahl daneben ist die belastbare Angabe. Die Fallzahl steht
 * NICHT nochmal hier — sie ist identisch mit der "Aufträge"-Spalte derselben
 * Zeile (ein "n = 70" neben "70 Aufträge" hat nur verwirrt, Matze 18.08.).
 */
export function QuoteZelle({ quote, datenLabel }: { quote: Quote; datenLabel?: string }) {
  const anteil = quote.anteil ?? 0;
  const farbe = quote.duenn ? FARBEN.tinteLeise : anteil >= 0.5 ? FARBEN.kritisch : FARBEN.ernst;
  return (
    <td data-label={datenLabel} style={{ padding: '0.35rem 0.5rem', borderBottom: `1px solid ${FARBEN.raster}`, minWidth: '130px' }}>
      <div
        title={`${prozent(quote.anteil)} der ${zahl(quote.n)} abgeschlossenen Aufträge kamen später als zuerst geplant`}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
      >
        <div style={{ flex: 1, height: '6px', background: FARBEN.raster, borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ width: `${Math.round(anteil * 100)}%`, height: '100%', background: farbe, borderRadius: '3px' }} />
        </div>
        <span style={{ fontVariantNumeric: 'tabular-nums', color: FARBEN.tinteStark, minWidth: '3.2rem', textAlign: 'right' }}>
          {prozent(quote.anteil)}
        </span>
      </div>
    </td>
  );
}

/** Sichtbare Markierung fuer duenne Datenbasis — nie verstecken, nie beschoenigen. */
export function DuennMarke() {
  return (
    <span
      title="Weniger als 10 abgeschlossene Fälle — die Prozentzahl kann durch einen einzigen Auftrag stark springen. Nur als Tendenz lesen."
      style={{
        fontSize: '0.65rem', color: FARBEN.tinteLeise, border: `1px dashed ${FARBEN.linie}`,
        borderRadius: '4px', padding: '0 0.3rem', marginLeft: '0.35rem', whiteSpace: 'nowrap',
        cursor: 'help',
      }}
    >
      wenig Fälle
    </span>
  );
}
